import React, { useEffect, useRef, useState } from "react";
import {
  DatePicker, Input, Select, Space, Table, Tooltip,
} from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { fetchMlRoutingRecords } from "../../../../actions/dashboard";
import { getSearchOptions } from "../../../../actions/batchReports";
import { formatAntdDateRange } from "../../../../utils/helpers";
import {
  extractMlRoutingResultList,
  mapMlRoutingApiRow,
} from "./mlRoutingUtils";
import MlRoutingDetailModal from "./MlRoutingDetailModal";
import "../../QC/QCFilterBar.css";
import "../../QC/QC.css";
import "./DashboardMlRouting.css";

const { RangePicker } = DatePicker;

const defaultCustomerOption = { value: "", label: "All Customers" };

const ML_ROUTING_STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "pending", label: "Pending" },
];

const dateFormat = "MM/DD/YYYY";
const defaultPageSize = 10;

/** Today only (single-day default for Workorder Date Range). */
const getDefaultMlRoutingDateRange = () => [
  dayjs(),
  dayjs(),
];

const renderEllipsisCell = (text) => {
  const display = text ?? "-";
  return (
    <Tooltip title={display} placement="topLeft">
      <span className="DashboardMlRouting__CellText">{display}</span>
    </Tooltip>
  );
};

const mlRoutingColumns = [
  {
    title: "Customer ID",
    dataIndex: "customer_id",
    key: "customer_id",
    width: 140,
    ellipsis: true,
  },
  {
    title: "WO",
    dataIndex: "workorder_id",
    key: "workorder_id",
    width: 110,
    ellipsis: true,
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
    width: 130,
    ellipsis: true,
  },
  {
    title: "Test",
    dataIndex: "test",
    key: "test",
    width: 160,
    ellipsis: true,
  },
  {
    title: "ML Status",
    dataIndex: "status",
    key: "status",
    width: 110,
    ellipsis: true,
  },
  {
    title: "ML Flags",
    dataIndex: "flags",
    key: "flags",
    width: 180,
    ellipsis: { showTitle: false },
    render: renderEllipsisCell,
  },
  {
    title: "ML Reasons",
    dataIndex: "reasons",
    key: "reasons",
    width: 280,
    ellipsis: { showTitle: false },
    render: renderEllipsisCell,
  },
];

const ML_ROUTING_TABLE_SCROLL_X = mlRoutingColumns.reduce(
  (total, column) => total + (column.width || 160),
  0,
);

export default function DashboardMlRouting() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateRange, setDateRange] = useState(getDefaultMlRoutingDateRange);
  const [customerList, setCustomerList] = useState([defaultCustomerOption]);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: 0,
    pageSizeOptions: ["10", "50", "100"],
  });

  const isMountedRef = useRef(true);
  const fetchGenerationRef = useRef(0);

  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  useEffect(() => {
    isMountedRef.current = true;
    const loadCustomers = async () => {
      const response = await getSearchOptions();
      if (!isMountedRef.current) {
        return;
      }
      const result = [defaultCustomerOption];
      if (response && response.success) {
        response.list.forEach((item) => {
          result.push({
            value: item.customer_id,
            label: `${item.company} (${item.customer_id})`,
          });
        });
        setCustomerList(result);
      } else if (response?.message) {
        toast.info(response.message);
      }
      setCustomerLoading(false);
    };
    loadCustomers();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const generation = fetchGenerationRef.current + 1;
    fetchGenerationRef.current = generation;
    const abortController = new AbortController();

    const loadRecords = async () => {
      setLoading(true);
      setData([]);
      setPaginationConfig((prev) => ({ ...prev, current: 1 }));
      const payload = {
        search,
        customer_id: selectedCustomer,
        date_range: formatAntdDateRange(dateRange),
        ...(selectedStatus ? { status: selectedStatus } : {}),
      };
      const response = await fetchMlRoutingRecords(payload, abortController.signal);
      if (fetchGenerationRef.current !== generation || !isMountedRef.current) {
        return;
      }
      if (response.canceled) {
        setLoading(false);
        return;
      }
      if (response.success) {
        const resultList = extractMlRoutingResultList(response);
        const tableRows = resultList.map((item, index) => mapMlRoutingApiRow(item, index));
        setData(tableRows);
        setPaginationConfig((prev) => ({
          ...prev,
          total: tableRows.length,
          current: 1,
        }));
      } else {
        toast.error(response.message || "Failed to fetch ML routing records");
        setData([]);
        setPaginationConfig((prev) => ({ ...prev, total: 0, current: 1 }));
      }
      setLoading(false);
    };

    loadRecords();

    return () => {
      abortController.abort();
    };
  }, [search, selectedCustomer, selectedStatus, dateRange]);

  const onFilterSearchChange = (value) => {
    setFilterSearch(value);
    if (value === "") {
      setSearch("");
    }
  };

  const handleFilterSearch = (value) => {
    setSearch(value.trim());
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value || getDefaultMlRoutingDateRange());
  };

  return (
    <div className="QCContainer DashboardMlRouting">
      <Space className="QCFilterBar" wrap>
        <div className="field-container">
          <label>Search</label>
          <Input.Search
            allowClear
            placeholder="Search by WO, Genista ID, Test"
            className="QCFilterSearch"
            value={filterSearch}
            onChange={(e) => onFilterSearchChange(e.target.value)}
            onSearch={handleFilterSearch}
          />
        </div>
        <div className="field-container">
          <label>Workorder Date Range</label>
          <RangePicker
            allowClear={false}
            className="QCFilterDate"
            format={dateFormat}
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </div>
        <div className="field-container">
          <label>Customer</label>
          <Select
            allowClear
            showSearch
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            loading={customerLoading}
            options={customerList}
            value={selectedCustomer}
            onSelect={(v) => setSelectedCustomer(v)}
            onClear={() => setSelectedCustomer("")}
            filterOption={(inputValue, option) => option.label?.toLowerCase().includes(inputValue?.toLowerCase())}
            listHeight={400}
          />
        </div>
        <div className="field-container">
          <label>ML Status</label>
          <Select
            allowClear
            className="QCFilterBarCategory"
            popupClassName="QCFilterBarCategory__Menu"
            options={ML_ROUTING_STATUS_OPTIONS}
            value={selectedStatus}
            onSelect={(v) => setSelectedStatus(v)}
            onClear={() => setSelectedStatus("")}
            listHeight={400}
          />
        </div>
      </Space>
      <div className="DashboardMlRouting__TableWrap">
        <Table
          bordered
          className="QCTable DashboardMlRouting__Table"
          dataSource={data}
          columns={mlRoutingColumns}
          pagination={{ ...paginationConfig, onChange: handlePageChange }}
          loading={loading}
          tableLayout="fixed"
          scroll={{ x: ML_ROUTING_TABLE_SCROLL_X }}
          onRow={(record) => ({
            onClick: () => setSelectedRecord(record),
            className: "QCTableRow",
          })}
        />
      </div>
      <MlRoutingDetailModal
        open={Boolean(selectedRecord)}
        record={selectedRecord}
        onCancel={() => setSelectedRecord(null)}
      />
    </div>
  );
}
